import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase';

const COLLECTION_NAME = 'userGoals';

// Default goals structure for new users
const getDefaultGoals = () => ({
  Sales: [
    {
      id: 1,
      metricName: 'Annual Revenue',
      description: 'Total annual revenue target',
      target: 0,
      actual: 0,
      unit: 'usd',
      deadline: '2024-12-31'
    },
    {
      id: 2,
      metricName: 'Closing Rate',
      description: 'Qualified Lead to customer conversion rate',
      target: 0,
      actual: 0,
      unit: 'percent',
      deadline: '2024-12-31'
    }
  ],
  Operations: [
    {
      id: 4,
      metricName: 'Gross Profit Margin',
      description: 'Gross profit margin percentage',
      target: 0,
      actual: 0,
      unit: 'percent',
      deadline: '2024-12-31'
    },
    {
      id: 5,
      metricName: 'Slippage %',
      description: 'Project timeline slippage percentage',
      target: 0,
      actual: 0,
      unit: 'percent',
      deadline: '2024-12-31'
    }
  ],
  Technology: [
    {
      id: 8,
      metricName: 'Active Automations',
      description: 'Number of active automation workflows',
      target: 0,
      actual: 0,
      unit: 'number',
      deadline: '2024-12-31'
    },
    {
      id: 9,
      metricName: 'Tech Stack Costs',
      description: 'Monthly technology stack costs',
      target: 0,
      actual: 0,
      unit: 'usd',
      deadline: '2024-12-31'
    }
  ],
  Finance: [
    {
      id: 10,
      metricName: 'Overhead',
      description: 'Monthly overhead costs',
      target: 0,
      actual: 0,
      unit: 'usd',
      deadline: '2024-12-31'
    },
    {
      id: 11,
      metricName: 'Net Profit',
      description: 'Monthly net profit target',
      target: 0,
      actual: 0,
      unit: 'usd',
      deadline: '2024-12-31'
    }
  ],
  Marketing: [
    {
      id: 13,
      metricName: 'Leads',
      description: 'Monthly leads generated',
      target: 0,
      actual: 0,
      unit: 'number',
      deadline: '2024-12-31'
    },
    {
      id: 16,
      metricName: 'Cost Per Lead',
      description: 'Average cost to acquire one qualified lead',
      target: 0,
      actual: 0,
      unit: 'usd',
      deadline: '2024-12-31'
    }
  ]
});

// Get user goals - creates default structure if none exists
export const getUserGoals = async (userId) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data().goals;
    } else {
      // Create default structure and save it to Firestore
      const defaultGoals = getDefaultGoals();
      const userGoalsData = {
        userId: userId,
        goals: defaultGoals,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      await setDoc(docRef, userGoalsData);
      return defaultGoals;
    }
  } catch (error) {
    console.error('Error getting user goals:', error);
    throw error;
  }
};

// Save entire goals structure for a user
export const saveUserGoals = async (userId, goals) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    await setDoc(docRef, {
      userId: userId,
      goals: goals,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error saving user goals:', error);
    throw error;
  }
};

// Update a specific goal
export const updateUserGoal = async (userId, department, goalId, updates) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const currentData = docSnap.data();
      
      // Ensure goals structure exists
      if (!currentData.goals) {
        currentData.goals = getDefaultGoals();
      }
      
      // Ensure the department array exists
      if (!currentData.goals[department]) {
        currentData.goals[department] = [];
      }
      
      const updatedGoals = {
        ...currentData.goals,
        [department]: currentData.goals[department].map(goal => 
          goal.id === goalId ? { ...goal, ...updates } : goal
        )
      };
      
      await setDoc(docRef, {
        ...currentData,
        goals: updatedGoals,
        updatedAt: serverTimestamp()
      });
      
      return updatedGoals;
    } else {
      throw new Error('User goals document not found');
    }
  } catch (error) {
    console.error('Error updating user goal:', error);
    throw error;
  }
};

// Add a new goal to a department
export const addUserGoal = async (userId, department, newGoal) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const currentData = docSnap.data();
      
      // Ensure goals structure exists
      if (!currentData.goals) {
        currentData.goals = getDefaultGoals();
      }
      
      // Ensure the department array exists
      if (!currentData.goals[department]) {
        currentData.goals[department] = [];
      }
      
      const updatedGoals = {
        ...currentData.goals,
        [department]: [...currentData.goals[department], newGoal]
      };
      
      await setDoc(docRef, {
        ...currentData,
        goals: updatedGoals,
        updatedAt: serverTimestamp()
      });
      
      return updatedGoals;
    } else {
      throw new Error('User goals document not found');
    }
  } catch (error) {
    console.error('Error adding user goal:', error);
    throw error;
  }
};

// Delete a goal from a department
export const deleteUserGoal = async (userId, department, goalId) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const currentData = docSnap.data();
      
      // Ensure goals structure exists
      if (!currentData.goals) {
        currentData.goals = getDefaultGoals();
      }
      
      // Ensure the department array exists
      if (!currentData.goals[department]) {
        currentData.goals[department] = [];
      }
      
      const updatedGoals = {
        ...currentData.goals,
        [department]: currentData.goals[department].filter(goal => goal.id !== goalId)
      };
      
      await setDoc(docRef, {
        ...currentData,
        goals: updatedGoals,
        updatedAt: serverTimestamp()
      });
      
      return updatedGoals;
    } else {
      throw new Error('User goals document not found');
    }
  } catch (error) {
    console.error('Error deleting user goal:', error);
    throw error;
  }
};

// Update multiple goals at once (for bulk operations)
export const updateMultipleGoals = async (userId, goals) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    await setDoc(docRef, {
      userId: userId,
      goals: goals,
      updatedAt: serverTimestamp()
    }, { merge: true });
  } catch (error) {
    console.error('Error updating multiple goals:', error);
    throw error;
  }
};

// Get dashboard goals data (revenue and leads)
export const getDashboardGoals = async (userId) => {
  try {
    const userGoals = await getUserGoals(userId);
    
    // Find revenue goal from Sales department
    const revenueGoal = userGoals.Sales?.find(goal => 
      goal.metricName.toLowerCase().includes('revenue') || 
      goal.metricName.toLowerCase().includes('annual revenue')
    );
    
    // Find leads goal from Marketing department
    const leadsGoal = userGoals.Marketing?.find(goal => 
      goal.metricName.toLowerCase().includes('leads')
    );
    
    return {
      revenue: {
        goal: revenueGoal?.target || 0,
        current: revenueGoal?.actual || 0,
        unit: revenueGoal?.unit || 'usd'
      },
      leads: {
        goal: leadsGoal?.target || 0,
        current: leadsGoal?.actual || 0,
        unit: leadsGoal?.unit || 'number'
      }
    };
  } catch (error) {
    console.error('Error getting dashboard goals:', error);
    // Return default values if there's an error
    return {
      revenue: { goal: 0, current: 0, unit: 'usd' },
      leads: { goal: 0, current: 0, unit: 'number' }
    };
  }
};